import {
  BookOpen,
  StickyNote,
  Target,
  Folder,
  type LucideIcon,
} from 'lucide-react';
import { useSlidingPill } from '@ai-tutor/ui';
import type { LessonTab, LessonProgress } from '@/types/lesson';

/** The active tab's fill, carried by the sliding pill rather than the button. */
const PILL_CLASS = 'rounded-md bg-secondary shadow-sm';

/**
 * The four lesson tabs, in order. Shared with `LessonBottomTabs`, the phone
 * placement of the same bar, so the two cannot drift apart.
 */
export const LESSON_TABS: readonly {
  id: LessonTab;
  label: string;
  icon: LucideIcon;
}[] = [
  { id: 'lesson', label: 'Lesson', icon: BookOpen },
  { id: 'practice', label: 'Practice', icon: Target },
  { id: 'notes', label: 'Notes', icon: StickyNote },
  { id: 'reference', label: 'Appendix', icon: Folder },
];

interface LessonTabsProps {
  activeTab: LessonTab;
  onTabChange: (tab: LessonTab) => void;
  progress: LessonProgress;
  hasNewContent?: boolean;
}

export function LessonTabs({
  activeTab,
  onTabChange,
  progress,
  hasNewContent = false,
}: LessonTabsProps) {
  const { containerRef, pill } = useSlidingPill(activeTab, PILL_CLASS);
  const tabs = LESSON_TABS.map(tab => ({
    ...tab,
    progress: progress[tab.id],
    enabled: true,
  }));

  return (
    <div
      ref={containerRef}
      className='relative mt-1 flex gap-1 rounded-lg p-1.5'
    >
      {pill}
      {tabs.map(tab => {
        const Icon = tab.icon;
        const isActive = activeTab === tab.id;

        return (
          <button
            key={tab.id}
            onClick={() => onTabChange(tab.id)}
            disabled={!tab.enabled}
            // Which tab is active was conveyed by background colour alone —
            // invisible to a screen reader, and to anyone who cannot
            // distinguish the two greys. `aria-current` is the minimal honest
            // fix; full `role="tablist"`/`tabpanel"` semantics would also need
            // arrow-key navigation and panel wiring, which is a larger change
            // than this one deserves on its own.
            aria-current={isActive ? 'true' : undefined}
            data-pill-active={isActive ? 'true' : undefined}
            className={`relative z-10 flex flex-1 items-center justify-center gap-2 rounded-md px-3 py-2.5 text-sm font-medium transition-colors duration-200 ${
              isActive
                ? 'text-foreground'
                : tab.enabled
                  ? 'text-muted-foreground hover:bg-muted hover:text-foreground dark:hover:bg-accent'
                  : 'cursor-not-allowed text-muted-foreground'
            }`}
          >
            <Icon className='h-4 w-4' />
            <span>{tab.label}</span>

            {/* New content indicator */}
            {hasNewContent && isActive && (
              <div className='absolute -right-1 -top-1 h-2 w-2 rounded-full bg-orange-500' />
            )}
          </button>
        );
      })}
    </div>
  );
}
